import { useState, useEffect } from 'react';
import { fetchEmployees, type EmployeeListItem } from '../api/employees';

export interface UseEmployeesOptions {
  autoFetch?: boolean;
  searchTerm?: string;
  statusFilter?: string;
}

export function useEmployees(options: UseEmployeesOptions = {}) {
  const { autoFetch = true, searchTerm = '', statusFilter = '' } = options;
  
  const [employees, setEmployees] = useState<EmployeeListItem[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const loadEmployees = async () => {
    try {
      setLoading(true);
      setError(null);
      const data = await fetchEmployees();
      if (!Array.isArray(data)) {
        throw new Error('Format inattendu (liste attendue)');
      }
      
      // Filtrage côté client (peut être déplacé côté backend plus tard)
      let filtered = data;
      
      if (searchTerm) {
        const term = searchTerm.toLowerCase();
        filtered = filtered.filter(emp => {
          const fullName = (emp.full_name || '').toLowerCase();
          const empId = (emp.employee_id || '').toLowerCase();
          const position = (emp.position_display || '').toLowerCase();
          const dept = (emp.department_name || '').toLowerCase();
          return (
            fullName.includes(term) ||
            empId.includes(term) ||
            position.includes(term) ||
            dept.includes(term)
          );
        });
      }
      
      if (statusFilter) {
        filtered = filtered.filter(emp => (emp.status || '') === statusFilter);
      }
      
      setEmployees(filtered);
    } catch (err: any) {
      console.error('Erreur chargement employés:', err);
      const message = err?.response?.data?.detail || err?.message || 'Erreur de chargement';
      setError(message);
      setEmployees([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (autoFetch) {
      loadEmployees();
    }
  }, [autoFetch]);

  return {
    employees,
    loading,
    error,
    refetch: loadEmployees,
  };
}
